import * as Sentry from '@sentry/react'
import { action, Action, State, thunk, Thunk } from 'easy-peasy'
import csAPI from '../../../API/csConfig'
import { Color } from '../../../chess/types'
import { ENABLE_PVP_EMOTES } from '../../../env'
import { createQueryParams } from '../../../sharedComponents/src/globalHeader/common/queryParams'
import { analyticsManager } from '../../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { getEmotesMuted } from '../../../sharedComponents/src/settings/emotesMuted'
import ReconnectingWebSocket from '../../../socket/ReconnectingWebsocket'
import { StoreModel } from '../../../store/store'
import { GS_REQUESTS } from '../components/gameServerConnector/GSTypes'
import { GameViewState } from '../GameViewModel'

// DEV-10077: env flag (main has no remote flags); an empty or failed catalogue hides emotes too.
export const EMOTES_ENABLED = ENABLE_PVP_EMOTES

const BUBBLE_MS = 5000
const REPLY_TIMEOUT_MS = 5000
const MAX_COOLDOWN_MS = 600000

export type EmoteMessage = {
    id: string
    content: string
    isGuestEnabled: boolean
    isMemberEnabled: boolean
    isSubscribedEnabled: boolean
}
export type EmotePhase = 'inGame' | 'endOfGame'
type EmoteBubbleData = { id: number; content: string; fromOpponent: boolean }
type SentEmote = { messageId: string; phase: EmotePhase }

export interface EmotesModel {
    // State
    matchId?: string // the PvP game this session (catalogue, bubbles, cooldown) belongs to
    messages: EmoteMessage[]
    loaded: boolean // the list request for this session finished (with or without messages)
    cooldownMs: number
    cooldownUntil: number
    cooldownTotal: number
    pending?: string // correlationId of the send that blocks the next one until its reply
    sent: Record<string, SentEmote> // by correlationId, waiting for the server reply
    bubbles: Record<string, EmoteBubbleData> // by playerId
    pickerOpen: boolean

    // Actions
    startSession: Action<EmotesModel, string | undefined>
    setCatalogue: Action<EmotesModel, { messages: EmoteMessage[]; cooldownMs: number }>
    addSent: Action<EmotesModel, { correlationId: string } & SentEmote>
    removeSent: Action<EmotesModel, string>
    startCooldown: Action<EmotesModel, number>
    setBubble: Action<EmotesModel, { playerId: string; bubble: EmoteBubbleData }>
    clearBubble: Action<EmotesModel, { playerId: string; id?: number }>
    clearOpponentBubbles: Action<EmotesModel>
    setPickerOpen: Action<EmotesModel, boolean>

    // Thunks
    fetchCatalogue: Thunk<EmotesModel, string, undefined, StoreModel>
    openPicker: Thunk<EmotesModel, undefined, undefined, StoreModel>
    sendEmote: Thunk<EmotesModel, string, undefined, StoreModel>
    receiveReply: Thunk<EmotesModel, any, undefined, StoreModel>
    receivePush: Thunk<EmotesModel, any, undefined, StoreModel>
    showBubble: Thunk<EmotesModel, { playerId: string; content: string; fromOpponent: boolean }>
    keepSocketForEmotes: Thunk<EmotesModel, ReconnectingWebSocket, undefined, StoreModel, boolean>
}

let bubbleSeq = 0

// Emote controls exist only in a PvP game whose emote list loaded and whose socket can carry them.
export const selectEmotesAvailable = (state: State<StoreModel>) => {
    const { gameView, emotes } = state
    const { gameData, gameState } = gameView
    if (!EMOTES_ENABLED || gameData?.matchKind !== 'human_vs_human' || !emotes.messages.length) return false
    if (emotes.matchId !== gameData.matchId) return false
    if (gameState === GameViewState.GAME_RUNNING) return !!gameView.socket
    if (gameState === GameViewState.GAME_OVER) return !!gameView.postGameSocket
    return false
}

// The in-game control row keeps the Emote slot from match start while the list loads, so the button does not
// pop in late; it only folds away once the list says there are no emotes for this game.
export const selectEmoteSlot = (state: State<StoreModel>): 'hidden' | 'pending' | 'ready' => {
    if (selectEmotesAvailable(state)) return 'ready'
    const { gameView, emotes } = state
    const { gameData, gameState } = gameView
    if (!EMOTES_ENABLED || gameData?.matchKind !== 'human_vs_human') return 'hidden'
    if (gameState !== GameViewState.GAME_PRELOADING && gameState !== GameViewState.GAME_RUNNING) return 'hidden'
    if (emotes.matchId === gameData.matchId && emotes.loaded && !emotes.messages.length) return 'hidden'
    return 'pending'
}

const newCorrelationId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

// me/opponent by colour, the same way the rest of the game view resolves them
const getPlayers = (storeState: State<StoreModel>) => {
    const { gameData, myColor } = storeState.gameView
    if (!gameData) return undefined
    const myColorName = myColor === Color.White ? 'white' : 'black'
    const me = gameData.player1.playerColor === myColorName ? gameData.player1 : gameData.player2
    const opponent = me === gameData.player1 ? gameData.player2 : gameData.player1
    return { gameData, me, opponent }
}

export const EmotesModel: EmotesModel = {
    // State
    matchId: undefined,
    messages: [],
    loaded: false,
    cooldownMs: 0,
    cooldownUntil: 0,
    cooldownTotal: 0,
    pending: undefined,
    sent: {},
    bubbles: {},
    pickerOpen: false,

    // Actions
    startSession: action((state, matchId) => {
        state.matchId = matchId
        state.messages = []
        state.loaded = false
        state.cooldownMs = 0
        state.cooldownUntil = 0
        state.cooldownTotal = 0
        state.pending = undefined
        state.sent = {}
        state.bubbles = {}
        state.pickerOpen = false
    }),

    setCatalogue: action((state, { messages, cooldownMs }) => {
        state.messages = messages
        state.cooldownMs = cooldownMs
        state.loaded = true
    }),

    addSent: action((state, { correlationId, ...sent }) => {
        state.sent[correlationId] = sent
        state.pending = correlationId
    }),

    removeSent: action((state, correlationId) => {
        delete state.sent[correlationId]
        if (state.pending === correlationId) state.pending = undefined
    }),

    startCooldown: action((state, ms) => {
        state.cooldownTotal = ms
        state.cooldownUntil = Date.now() + ms
    }),

    setBubble: action((state, { playerId, bubble }) => {
        state.bubbles[playerId] = bubble
    }),

    clearBubble: action((state, { playerId, id }) => {
        if (id === undefined || state.bubbles[playerId]?.id === id) delete state.bubbles[playerId]
    }),

    clearOpponentBubbles: action((state) => {
        Object.keys(state.bubbles).forEach((playerId) => {
            if (state.bubbles[playerId].fromOpponent) delete state.bubbles[playerId]
        })
    }),

    setPickerOpen: action((state, open) => {
        state.pickerOpen = open
    }),

    // Thunks
    fetchCatalogue: thunk(async (actions, matchId, { getState, getStoreState }) => {
        actions.startSession(matchId)
        const { token } = getStoreState()

        try {
            const { data } = await csAPI.get(`/api/game_messages${createQueryParams({ token })}`)
            if (getState().matchId !== matchId) return

            const messages: EmoteMessage[] = (Array.isArray(data?.messages) ? data.messages : [])
                .filter((m: any) => typeof m?.id === 'string' && typeof m?.content === 'string' && m.content)
                // a missing tier flag means the emote is open to that tier
                .map((m: any) => ({
                    id: m.id,
                    content: m.content,
                    isGuestEnabled: m.isGuestEnabled !== false,
                    isMemberEnabled: m.isMemberEnabled !== false,
                    isSubscribedEnabled: m.isSubscribedEnabled !== false,
                }))
            const cooldownMs = Math.min(Math.max(Number(data?.cooldownMs) || 0, 0), MAX_COOLDOWN_MS)
            actions.setCatalogue({ messages, cooldownMs })
        } catch (error) {
            // 401 / any error → no emotes for this game (the endpoint is not deployed everywhere yet, so no Sentry event)
            Sentry.addBreadcrumb({ category: 'emotes', level: 'info', message: `emote list unavailable: ${error}` })
            if (getState().matchId === matchId) actions.setCatalogue({ messages: [], cooldownMs: 0 })
        }
    }),

    openPicker: thunk((actions, _, { getState, getStoreState }) => {
        const { matchId, pickerOpen } = getState()
        if (!matchId || pickerOpen) return
        actions.setPickerOpen(true)
        analyticsManager.dispatchEvent('pvpEmoteMenuOpened', {
            matchID: matchId,
            phase: getStoreState().gameView.gameState === GameViewState.GAME_OVER ? 'endOfGame' : 'inGame',
        })
    }),

    sendEmote: thunk((actions, messageId, { getState, getStoreState }) => {
        const { matchId, messages, pending, cooldownUntil } = getState()
        const { gameView } = getStoreState()
        const socket = gameView.postGameSocket ?? gameView.socket

        if (!EMOTES_ENABLED || !matchId || pending || Date.now() < cooldownUntil) return
        if (!messages.some((m) => m.id === messageId)) return
        // ReconnectingWebSocket.send() queues while disconnected; an emote must never be replayed later
        if (!socket || socket.readyState !== ReconnectingWebSocket.OPEN) return

        const correlationId = newCorrelationId()
        const phase: EmotePhase = gameView.gameState === GameViewState.GAME_OVER ? 'endOfGame' : 'inGame'
        socket.send(
            JSON.stringify({ requestType: GS_REQUESTS.SEND_EMOTE, rawData: { matchId, messageId }, correlationId }),
        )
        actions.addSent({ correlationId, messageId, phase })
        // no reply in time: unblock sending and ignore a late reply (it would restart an older cooldown)
        setTimeout(() => actions.removeSent(correlationId), REPLY_TIMEOUT_MS)
    }),

    receiveReply: thunk((actions, frame, { getState, getStoreState }) => {
        const { sent, matchId, messages, cooldownMs } = getState()
        const correlationId: string | undefined = frame?.correlationId
        const request = correlationId ? sent[correlationId] : undefined
        // unknown, timed-out or already handled reply (dedupe), or a reply for a finished session
        if (!correlationId || !request) return
        actions.removeSent(correlationId)

        const data = frame.data ?? {}
        if (frame.result?.status !== 'success') {
            if (frame.result?.code === 'EMOTE_COOLDOWN' && Number(data.retryAfterMs) > 0) {
                actions.startCooldown(Math.min(Number(data.retryAfterMs), MAX_COOLDOWN_MS))
            }
            Sentry.addBreadcrumb({
                category: 'emotes',
                level: 'info',
                message: `emote rejected: ${frame.result?.code} ${frame.result?.errorMessage ?? ''}`,
            })
            return
        }

        const players = getPlayers(getStoreState())
        if (!players || (data.matchId && data.matchId !== matchId)) return

        const cooldown = Number(data.cooldownMs)
        actions.startCooldown(
            Math.min(Number.isFinite(cooldown) && cooldown >= 0 ? cooldown : cooldownMs, MAX_COOLDOWN_MS),
        )

        const content =
            typeof data.content === 'string' && data.content
                ? data.content
                : messages.find((m) => m.id === request.messageId)?.content
        if (content) actions.showBubble({ playerId: players.me.playerId, content, fromOpponent: false })

        const { gameData, opponent } = players
        analyticsManager.dispatchEvent('pvpEmoteSent', {
            emoteId: request.messageId,
            matchID: gameData.matchId,
            matchKind: gameData.matchKind,
            rated: gameData.rated,
            minutes: gameData.timeMode.durationMinutes,
            increment: gameData.timeMode.clockIncrementSeconds,
            opponentID: opponent.playerId,
            phase: request.phase,
        })
    }),

    receivePush: thunk((actions, frame, { getState, getStoreState }) => {
        const data = frame?.data
        const { matchId } = getState()
        if (!data || typeof data.content !== 'string' || !data.content) return
        if (!matchId || data.matchId !== matchId || getEmotesMuted()) return

        const players = getPlayers(getStoreState())
        if (!players || data.senderId === players.me.playerId) return
        actions.showBubble({ playerId: players.opponent.playerId, content: data.content, fromOpponent: true })
    }),

    // overlapping emotes from the same player replace each other
    showBubble: thunk((actions, { playerId, ...bubble }) => {
        const id = ++bubbleSeq
        actions.setBubble({ playerId, bubble: { id, ...bubble } })
        setTimeout(() => actions.clearBubble({ playerId, id }), BUBBLE_MS)
    }),

    // endOfGameMessage with postGame.emotes: keep this socket for emotes only, detached from the game.
    // A list still loading (fast game) keeps it too; only a loaded-but-empty list for this game does not.
    keepSocketForEmotes: thunk((actions, ws, { getState, getStoreState, getStoreActions }) => {
        const { matchId, messages, loaded } = getState()
        const { gameData } = getStoreState().gameView
        if (!EMOTES_ENABLED || gameData?.matchKind !== 'human_vs_human') return false
        if (matchId === gameData.matchId && loaded && !messages.length) return false

        ws.stopReconnecting()
        getStoreActions().gameView.detachGSConnection(ws)
        return true
    }),
}
